import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Modal,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Chip,
  Avatar,
  CircularProgress,
  Badge
} from '@mui/material';
import {
  ArrowBack,
  ExpandMore,
  Star,
  CardGiftcard,
  AutoAwesome,
  MenuBook,
  MusicNote,
  Close
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { fetchProducts } from '../api/products';
import NavBar from '../components/NavBar';
import { useTheme } from '@mui/material/styles';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { createCheckoutSession } from '../api/products';
import { useAuth } from '../contexts/AuthContext';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [giftForm, setGiftForm] = useState({
    recipientName: '',
    recipientEmail: '',
    duration: '1',
    message: '',
    sendDate: ''
  });
  const [personalizedForm, setPersonalizedForm] = useState({
    intention: '',
    language: 'english',
    style: 'traditional',
    personalInfo: ''
  });
  const [soundHealingForm, setSoundHealingForm] = useState({
    practitioner: '',
    preferredDate: '',
    preferredTime: '',
    focusArea: '',
    experience: '',
    phone: '',
    email: ''
  });
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const allProducts = await fetchProducts();
        setProducts(allProducts);
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  const handleOpenModal = (product) => {
    setSelectedProduct(product);
    if (product.type === 'premium-upgrade') {
      setPremiumModalOpen(true);
    } else if (product.type === 'personalized-mantra') {
      navigate('/personalized-mantra');
    } else if (product.type === 'ebooks') {
      navigate('/ebooks');
    } else {
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProduct(null);
    setGiftForm({ recipientName: '', recipientEmail: '', duration: '1', message: '', sendDate: '' });
    setPersonalizedForm({ intention: '', language: 'english', style: 'traditional', personalInfo: '' });
    setSoundHealingForm({ practitioner: '', preferredDate: '', preferredTime: '', focusArea: '', experience: '', phone: '', email: '' });
  };

  const handleClosePremiumModal = () => {
    setPremiumModalOpen(false);
    setSelectedProduct(null);
  };

  const ProductCard = ({ product }) => {
    const getProductIcon = () => {
      switch (product.type) {
        case 'premium-upgrade':
          return <Star sx={{ fontSize: 40, color: '#FFD700' }} />;
        case 'gift-premium':
          return <CardGiftcard sx={{ fontSize: 40, color: '#F4A261' }} />;
        case 'personalized-mantra':
          return <AutoAwesome sx={{ fontSize: 40, color: '#90E0EF' }} />;
        case 'ebooks':
          return <MenuBook sx={{ fontSize: 40, color: '#6A4C93' }} />;
        case 'sound-healing':
          return <MusicNote sx={{ fontSize: 40, color: '#E63946' }} />;
        default:
          return null;
      }
    };

    const handleCardAction = () => {
      handleOpenModal(product);
    };

    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6
          }
        }}
      >
        {product.popular && (
          <Chip
            label="Popular"
            color="secondary"
            size="small"
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 2,
              cursor: 'default'
            }}
          />
        )}
        <CardContent sx={{ flexGrow: 1, p: 3, pt: product.popular ? 5 : 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            {getProductIcon()}
            <Box ml={2} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={600} sx={{ pr: product.popular ? 6 : 0 }}>
                {product.title}
              </Typography>
              <Typography variant="h6" color="primary" fontWeight={600}>
                {product.price}
              </Typography>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            {product.description}
          </Typography>

          {product.features && (
            <List dense sx={{ mt: 2 }}>
              {product.features.slice(0, 3).map((feature, index) => (
                <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                  <ListItemText
                    primary={`• ${feature}`}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
              {product.features.length > 3 && (
                <ListItem sx={{ px: 0, py: 0.5 }}>
                  <ListItemText
                    primary={`+ ${product.features.length - 3} more features`}
                    primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
              )}
            </List>
          )}

          {product.type === 'ebooks' && (
            <Typography variant="body2" color="text.secondary" mt={2}>
              {product.ebooks.length} eBooks available
            </Typography>
          )}

          {product.deliveryTime && (
            <Typography variant="caption" color="text.secondary" mt={2} display="block">
              Delivery: {product.deliveryTime}
            </Typography>
          )}
        </CardContent>

        <CardActions sx={{ p: 3, pt: 0 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleCardAction}
            size="large"
          >
            {product.cta}
          </Button>
        </CardActions>
      </Card>
    );
  };

  const GiftModal = () => (
    <Box
      sx={{
        p: { xs: 2, sm: 4 },
        bgcolor: 'background.paper',
        borderRadius: 3,
        maxWidth: 600,
        mx: 'auto',
        mt: 4,
        boxShadow: 8,
        minWidth: { xs: '90vw', sm: 400 },
        display: 'grid',
        gridTemplateRows: 'auto auto 1fr auto',
        rowGap: { xs: 2, sm: 3 },
        alignItems: 'center',
      }}
    >
      <Typography variant="h5" fontWeight={700} color="primary" gutterBottom sx={{ textAlign: 'center', mb: 0 }}>
        Gift Premium Membership
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        Send the gift of wellness to someone special
      </Typography>
      <Box sx={{ width: '100%' }}>
        <Box
          component="form"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mb: 2,
          }}
          noValidate
          autoComplete="off"
        >
          <TextField
            fullWidth
            label="Recipient's Name"
            value={giftForm.recipientName}
            onChange={(e) => setGiftForm({ ...giftForm, recipientName: e.target.value })}
          />
          <TextField
            fullWidth
            label="Recipient's Email"
            type="email"
            value={giftForm.recipientEmail}
            onChange={(e) => setGiftForm({ ...giftForm, recipientEmail: e.target.value })}
          />
          <FormControl fullWidth>
            <InputLabel>Gift Duration</InputLabel>
            <Select
              value={giftForm.duration}
              label="Gift Duration"
              onChange={(e) => setGiftForm({ ...giftForm, duration: e.target.value })}
            >
              <MenuItem value="1">1 Month - $19.99</MenuItem>
              <MenuItem value="3">3 Months - $54.99</MenuItem>
              <MenuItem value="6">6 Months - $99.99</MenuItem>
              <MenuItem value="12">12 Months - $179.99</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Send Date (Optional)"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={giftForm.sendDate}
            onChange={(e) => setGiftForm({ ...giftForm, sendDate: e.target.value })}
          />
          <TextField
            fullWidth
            label="Personal Message"
            multiline
            rows={3}
            value={giftForm.message}
            onChange={(e) => setGiftForm({ ...giftForm, message: e.target.value })}
            placeholder="Write a heartfelt message to accompany your gift..."
            sx={{ gridColumn: { xs: '1', sm: '1 / span 2' } }}
          />
        </Box>
        <Box
          sx={{
            bgcolor: 'rgba(69,123,157,0.07)',
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
            fontStyle: 'italic',
            color: 'primary.main',
            fontWeight: 500,
            mb: 2
          }}
        >
          Stripe payment integration coming soon.
        </Box>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
          width: '100%'
        }}
      >
        <Button variant="outlined" onClick={handleCloseModal} sx={{ borderRadius: 999 }}>
          Cancel
        </Button>
        <Button variant="contained" sx={{ borderRadius: 999 }}>
          Send Gift
        </Button>
      </Box>
    </Box>
  );

  const PersonalizedMantraModal = () => (
    <Box
      sx={{
        p: { xs: 2, sm: 4 },
        bgcolor: 'background.paper',
        borderRadius: 3,
        maxWidth: 600,
        mx: 'auto',
        mt: 4,
        boxShadow: 8,
        minWidth: { xs: '90vw', sm: 400 },
        display: 'grid',
        gridTemplateRows: 'auto auto 1fr auto',
        rowGap: { xs: 2, sm: 3 },
        alignItems: 'center',
      }}
    >
      <Typography variant="h5" fontWeight={700} color="primary" gutterBottom sx={{ textAlign: 'center', mb: 0 }}>
        Create Your Personal Mantra
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        Work with our practitioners to create a mantra uniquely for you
      </Typography>
      <Box sx={{ width: '100%' }}>
        <Box
          component="form"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mb: 2,
          }}
          noValidate
          autoComplete="off"
        >
          <TextField
            fullWidth
            label="Your Intention/Goal"
            multiline
            rows={3}
            value={personalizedForm.intention}
            onChange={(e) => setPersonalizedForm({ ...personalizedForm, intention: e.target.value })}
            placeholder="What would you like your mantra to help you achieve?"
            sx={{ gridColumn: { xs: '1', sm: '1 / span 2' } }}
          />
          <FormControl fullWidth>
            <InputLabel>Preferred Language</InputLabel>
            <Select
              value={personalizedForm.language}
              label="Preferred Language"
              onChange={(e) => setPersonalizedForm({ ...personalizedForm, language: e.target.value })}
            >
              <MenuItem value="english">English</MenuItem>
              <MenuItem value="sanskrit">Sanskrit</MenuItem>
              <MenuItem value="mixed">Mixed (English & Sanskrit)</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Mantra Style</InputLabel>
            <Select
              value={personalizedForm.style}
              label="Mantra Style"
              onChange={(e) => setPersonalizedForm({ ...personalizedForm, style: e.target.value })}
            >
              <MenuItem value="traditional">Traditional</MenuItem>
              <MenuItem value="modern">Modern</MenuItem>
              <MenuItem value="affirmation">Affirmation-based</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Tell Us About Yourself"
            multiline
            rows={3}
            value={personalizedForm.personalInfo}
            onChange={(e) => setPersonalizedForm({ ...personalizedForm, personalInfo: e.target.value })}
            placeholder="Share any personal details that might help us create the perfect mantra for you..."
            sx={{ gridColumn: { xs: '1', sm: '1 / span 2' } }}
          />
        </Box>
        <Box
          sx={{
            bgcolor: 'rgba(69,123,157,0.07)',
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
            fontStyle: 'italic',
            color: 'primary.main',
            fontWeight: 500,
            mb: 2
          }}
        >
          Stripe payment integration coming soon.
        </Box>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
          width: '100%'
        }}
      >
        <Button variant="outlined" onClick={handleCloseModal} sx={{ borderRadius: 999 }}>
          Cancel
        </Button>
        <Button variant="contained" sx={{ borderRadius: 999 }}>
          Request Consultation
        </Button>
      </Box>
    </Box>
  );

  const SoundHealingModal = () => (
    <Box
      sx={{
        p: { xs: 2, sm: 4 },
        bgcolor: 'background.paper',
        borderRadius: 3,
        maxWidth: 700,
        mx: 'auto',
        mt: 4,
        boxShadow: 8,
        minWidth: { xs: '90vw', sm: 400 },
        display: 'grid',
        gridTemplateRows: 'auto auto 1fr auto',
        rowGap: { xs: 2, sm: 3 },
        alignItems: 'center',
      }}
    >
      <Typography variant="h5" fontWeight={700} color="primary" gutterBottom sx={{ textAlign: 'center', mb: 0 }}>
        Book Sound Healing Session
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        1-to-1 sound healing offers a deeply personalized experience. Karen's sessions are in high demand — booking ahead is advised.
      </Typography>
      <Box sx={{ width: '100%' }}>
        {/* Karen's Info */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3,
            bgcolor: 'rgba(69,123,157,0.07)',
            borderRadius: 2,
            p: 2,
          }}
        >
          <img src="/karen1.png" alt="Karen" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '2px solid #90e0ef' }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="primary.main">Karen</Typography>
            <Typography variant="caption" color="text.secondary">Lead Sound Healer</Typography>
          </Box>
        </Box>
        <Box
          component="form"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mb: 2,
          }}
          noValidate
          autoComplete="off"
        >
          <TextField
            fullWidth
            label="Preferred Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={soundHealingForm.preferredDate}
            onChange={(e) => setSoundHealingForm({ ...soundHealingForm, preferredDate: e.target.value })}
          />
          <FormControl fullWidth>
            <InputLabel>Preferred Time</InputLabel>
            <Select
              value={soundHealingForm.preferredTime}
              label="Preferred Time"
              onChange={(e) => setSoundHealingForm({ ...soundHealingForm, preferredTime: e.target.value })}
            >
              <MenuItem value="morning">Morning (9 AM - 12 PM)</MenuItem>
              <MenuItem value="afternoon">Afternoon (12 PM - 5 PM)</MenuItem>
              <MenuItem value="evening">Evening (5 PM - 8 PM)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="What would you like to focus on?"
            value={soundHealingForm.focusArea}
            onChange={(e) => setSoundHealingForm({ ...soundHealingForm, focusArea: e.target.value })}
            placeholder="e.g., stress relief, chakra balancing, emotional healing..."
            sx={{ gridColumn: { xs: '1', sm: '1 / span 2' } }}
          />
          <TextField
            fullWidth
            label="Previous Sound Healing Experience"
            multiline
            rows={2}
            value={soundHealingForm.experience}
            onChange={(e) => setSoundHealingForm({ ...soundHealingForm, experience: e.target.value })}
            placeholder="Tell us about any previous experience or if this is your first session..."
            sx={{ gridColumn: { xs: '1', sm: '1 / span 2' } }}
          />
          <TextField
            fullWidth
            label="Phone Number"
            type="tel"
            value={soundHealingForm.phone}
            onChange={(e) => setSoundHealingForm({ ...soundHealingForm, phone: e.target.value })}
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={soundHealingForm.email}
            onChange={(e) => setSoundHealingForm({ ...soundHealingForm, email: e.target.value })}
          />
        </Box>
        <Box
          sx={{
            bgcolor: 'rgba(69,123,157,0.07)',
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
            fontStyle: 'italic',
            color: 'primary.main',
            fontWeight: 500,
            mb: 2
          }}
        >
          Stripe payment integration coming soon.
        </Box>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
          width: '100%'
        }}
      >
        <Button variant="outlined" onClick={handleCloseModal} sx={{ borderRadius: 999 }}>
          Cancel
        </Button>
        <Button variant="contained" sx={{ borderRadius: 999 }}>
          Request Session
        </Button>
      </Box>
    </Box>
  );

  const EBooksModal = () => (
    <Box
      sx={{
        p: { xs: 2, sm: 4 },
        bgcolor: 'background.paper',
        borderRadius: 3,
        maxWidth: 800,
        mx: 'auto',
        mt: 4,
        boxShadow: 8,
        minWidth: { xs: '90vw', sm: 400 },
        display: 'grid',
        gridTemplateRows: 'auto auto 1fr auto',
        rowGap: { xs: 2, sm: 3 },
        alignItems: 'center',
      }}
    >
      <Typography variant="h5" fontWeight={700} color="primary" gutterBottom sx={{ textAlign: 'center', mb: 0 }}>
        Wellness eBooks Collection
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        Expand your knowledge with our curated digital library
      </Typography>
      <Box sx={{ width: '100%' }}>
        {selectedProduct?.ebooks?.map((ebook, index) => (
          <Accordion key={index} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    {ebook.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {ebook.pages} pages
                  </Typography>
                </Box>
                <Typography variant="h6" color="primary" fontWeight={600}>
                  {ebook.price}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                {ebook.description}
              </Typography>
              <Box display="flex" gap={2}>
                <Button variant="outlined" size="small">
                  Preview
                </Button>
                <Button variant="contained" size="small">
                  Purchase
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
        <Box
          sx={{
            bgcolor: 'rgba(69,123,157,0.07)',
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
            fontStyle: 'italic',
            color: 'primary.main',
            fontWeight: 500,
            mb: 2
          }}
        >
          Stripe payment integration coming soon.
        </Box>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
          width: '100%'
        }}
      >
        <Button variant="outlined" onClick={handleCloseModal} sx={{ borderRadius: 999 }}>
          Close
        </Button>
        <Button variant="contained" sx={{ borderRadius: 999 }}>
          Buy All Books - $67.95
        </Button>
      </Box>
    </Box>
  );

  const PremiumUpgradeModal = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleUpgrade = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await createCheckoutSession('price_1RdoPVQiEcaYBI53UUE47FIP');
        console.log('data', data);
        if (!data.sessionId) throw new Error('No session ID returned');
        const stripe = await stripePromise;
        const { error } = await stripe.redirectToCheckout({ sessionId: data.sessionId });
        if (error) setError(error.message);
      } catch (err) {
        setError(err.message || 'Something went wrong.');
      } finally {
        setLoading(false);
      }
    };

    return (
      <Box
        sx={{
          p: { xs: 2, sm: 4 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          maxWidth: 500,
          mx: 'auto',
          mt: 4,
          boxShadow: 8,
          minWidth: { xs: '90vw', sm: 400 },
          display: 'grid',
          gridTemplateRows: 'auto auto 1fr auto',
          rowGap: { xs: 2, sm: 3 },
          alignItems: 'center',
        }}
      >
        <Typography variant="h5" fontWeight={700} color="primary" gutterBottom sx={{ textAlign: 'center', mb: 0 }}>
          Upgrade to Premium
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
          Unlock all premium features, exclusive content, and priority support. Enjoy a deeper, ad-free experience designed for your wellness journey.
        </Typography>
        {error && (
          <Box sx={{ color: 'error.main', textAlign: 'center', my: 1 }}>{error}</Box>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            width: '100%'
          }}
        >
          <Button variant="outlined" onClick={handleClosePremiumModal} sx={{ borderRadius: 999 }} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ borderRadius: 999, fontWeight: 600 }}
            onClick={handleUpgrade}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Confirm & Upgrade'}
          </Button>
        </Box>
      </Box>
    );
  };

  const renderModalContent = () => {
    if (!selectedProduct) return null;
    switch (selectedProduct.type) {
      case 'gift-premium':
        return <GiftModal />;
      case 'sound-healing':
        return <SoundHealingModal />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        py: 0
      }}
    >
      <NavBar user={user} />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Page Header */}
        <Box mb={4}>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Transform Your Wellness Journey
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Discover premium offerings designed to deepen your practice and inner peace
          </Typography>
        </Box>

        {/* Products Grid */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)'
              },
              gap: 4,
              alignItems: 'stretch'
            }}
          >
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </Box>
        )}
      </Container>

      {/* Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box sx={{ maxHeight: '90vh', overflowY: 'auto', outline: 'none' }}>
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary',
              zIndex: 1
            }}
          >
            <Close />
          </IconButton>
          {renderModalContent()}
        </Box>
      </Modal>

      <Modal
        open={premiumModalOpen}
        onClose={handleClosePremiumModal}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}
      >
        <Box sx={{ maxHeight: '90vh', overflowY: 'auto', outline: 'none', position: 'relative' }}>
          <IconButton
            onClick={handleClosePremiumModal}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary', zIndex: 1 }}
          >
            <Close />
          </IconButton>
          <PremiumUpgradeModal />
        </Box>
      </Modal>
    </Box>
  );
};

export default ProductsPage; 